import {
  AdditiveBlending,
  Box3,
  ClippingGroup,
  Color as ThreeColor,
  Group,
  IcosahedronGeometry,
  InstancedMesh,
  type Material,
  type Mesh,
  MeshBasicMaterial,
  Object3D,
  PerspectiveCamera,
  Plane,
  PointLight,
  SRGBColorSpace,
  Vector3,
} from "three/webgpu";
import { rgba, type Color } from "../../common/color.js";
import type { Point } from "../../common/geometry.js";
import { Model, type ModelInstance } from "../../engine3d/Model.js";
import { resolveColorRef, type ColorRef } from "../art/ArtControl.js";
import type { MouseEventArgs, WheelEventArgs } from "../core/events.js";
import type { Pinnable } from "../core/pin.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import { meshesOf, partOf, revealFront, sparkLife } from "./stageParts.js";
import { Viewport3D, type Viewport3DOptions } from "./Viewport3D.js";

export { REVEAL_SECONDS, revealFront, SPARK_SECONDS, sparkLife, partOf } from "./stageParts.js";

/** A point, or a box's corner, as plain numbers. */
interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/** Every model is shown at this height, in scene units, whatever its own units were. */
export const STAGE_HEIGHT = 1.8;

/** The named points a stage answers `pinPoint` for, as fractions of the model's height. */
export const STAGE_ANCHORS: Readonly<Record<string, number>> = {
  feet: 0,
  knees: 0.28,
  waist: 0.5,
  chest: 0.7,
  head: 0.92,
  above: 1.08,
};

export interface StageFit {
  /** Uniform scale that brings the model to `STAGE_HEIGHT`. */
  scale: number;
  /** Where to put the model's root so its feet stand on y = 0 and it is centered on the axis. */
  offset: Vec3;
}

/** How to stand a model with these bind-pose bounds on the stage. Pure. */
export function fitModel(bounds: { min: Vec3; max: Vec3 }): StageFit {
  const natural = Math.max(1e-6, bounds.max.y - bounds.min.y);
  const scale = STAGE_HEIGHT / natural;
  return {
    scale,
    offset: {
      x: -((bounds.min.x + bounds.max.x) / 2) * scale,
      y: -bounds.min.y * scale,
      z: -((bounds.min.z + bounds.max.z) / 2) * scale,
    },
  };
}

/** How big a model is made in orbit view: its longest side, in scene units. */
export const ORBIT_SPAN = 6;

/** How a stage shows its model: a standing figure, or a thing to walk the camera round. */
export type StageView = "stage" | "orbit";

/**
 * How to set a model down for orbiting: its longest side brought to `ORBIT_SPAN`, standing on y = 0,
 * centered. A building is fitted by its footprint as much as its height - fitted by height alone, a
 * bungalow would come out the size of a town. Pure.
 */
export function fitOrbit(bounds: { min: Vec3; max: Vec3 }): StageFit & { height: number; radius: number } {
  const dx = bounds.max.x - bounds.min.x;
  const dy = bounds.max.y - bounds.min.y;
  const dz = bounds.max.z - bounds.min.z;
  const scale = ORBIT_SPAN / Math.max(1e-6, dx, dy, dz);
  return {
    scale,
    offset: {
      x: -((bounds.min.x + bounds.max.x) / 2) * scale,
      y: -bounds.min.y * scale,
      z: -((bounds.min.z + bounds.max.z) / 2) * scale,
    },
    height: dy * scale,
    radius: (Math.hypot(dx, dy, dz) / 2) * scale,
  };
}

/**
 * Where an orbit starts: looking down at three-quarters, far enough back that a sphere round the whole
 * model fits the field of view (`fov` in radians). Pure.
 */
export function orbitStart(fov: number, height: number, radius: number): { position: Vec3; target: Vec3; distance: number } {
  // A sphere round a floor plan - wide and flat - is a far frame: two-thirds of that distance still has
  // the plan's footprint in view at the starting angle.
  const distance = (radius / Math.sin(fov / 2)) * 0.68;
  const yaw = 0.7;
  const pitch = 0.5;
  const target = { x: 0, y: height * 0.4, z: 0 };
  return {
    position: {
      x: target.x + distance * Math.cos(pitch) * Math.sin(yaw),
      y: target.y + distance * Math.sin(pitch),
      z: target.z + distance * Math.cos(pitch) * Math.cos(yaw),
    },
    target,
    distance,
  };
}

/**
 * Where the camera stands at `t` seconds: far enough back that the whole figure and a margin fit the
 * vertical field of view (`fov` in radians), looking at a point a little under its middle, and swaying
 * by `drift` so the scene breathes. Pure.
 */
export function stageCamera(fov: number, t: number, drift: number): { position: Vec3; target: Vec3 } {
  // Room round the figure for a title above it and a row of buttons below.
  const framed = STAGE_HEIGHT * 1.9;
  const distance = framed / 2 / Math.tan(fov / 2);
  const sway = Math.sin(t * 0.18) * 0.12 * drift;
  return {
    position: {
      x: Math.sin(sway) * distance,
      y: STAGE_HEIGHT * 0.83 + Math.sin(t * 0.27) * 0.08 * drift,
      z: Math.cos(sway) * distance,
    },
    target: { x: 0, y: STAGE_HEIGHT * 0.5, z: 0 },
  };
}

export interface ModelStageOptions extends Omit<Viewport3DOptions, "orbitOnDrag" | "zoomOnWheel"> {
  /** A glTF / GLB file. Loaded once the stage is on a desktop. */
  src?: string | null;
  /** Clips to play, most wanted first, comma separated - rigs disagree on what idle is called. */
  idle?: string;
  /** Whether dragging turns the model. */
  turnable?: boolean;
  /** How much the camera drifts, 0 to hold it still. */
  drift?: number;
  /** A colored light from below and behind - the fire a hero stands over. Null for none. */
  glow?: ColorRef | null;
  /** Radians a second the model turns on its own when nobody is holding it. */
  spin?: number;
  /**
   * `stage` (the default) stands the model as a figure and a drag turns it; `orbit` sets it down to be
   * walked round - drag to orbit the camera, Ctrl + wheel to come closer - which is what a house wants.
   */
  view?: StageView;
  /**
   * The named parts a click can pick: node names from the model, compared without case. `*` means every
   * named node. Empty or absent, nothing is clickable.
   */
  parts?: readonly string[] | null;
  /** The color a hovered and a picked part are washed with. The theme's accent when absent. */
  highlight?: ColorRef | null;
  /** Called when a click lands on a part, or on nothing (null). */
  onPartClick?: ((part: string | null) => void) | null;
  /** An outline round the picked and hovered parts, in this color. Null (the default) washes them instead. */
  outline?: ColorRef | null;
  /** How far the rest of the model fades when a part is picked, 0 (not at all) to 1. */
  ghost?: number;
  /** Sweep the model in from the floor up when it arrives. */
  reveal?: boolean;
  /** A burst of sparks from a part when it is picked. */
  sparks?: boolean;
}

const TURN_PER_PIXEL = 0.012;
/** A press that travels further than this is a drag, not a click. */
const CLICK_SLOP = 5;
const SPARKS = 96;

/** Each washed mesh's own material, and the one it had before any wash. */
interface Washed {
  own: Material & { color?: ThreeColor; emissive?: ThreeColor; emissiveIntensity?: number; opacity: number };
  base: Material & { color?: ThreeColor; emissive?: ThreeColor; emissiveIntensity?: number };
}

/**
 * A 3D model on a scene's stage: one glTF, standing, breathing, turning when dragged.
 *
 * A transparent viewport so whatever is painted behind shows round the figure, a camera framed on the
 * model and drifting slowly, an idle clip, and a drag that turns the figure rather than orbiting the
 * camera.
 *
 * It is `Pinnable`: `pinPoint("feet")` is where the model's feet are on screen this frame, which is what
 * lets a painted ring stand under it and a name float over its head from another band of the scene.
 */
export class ModelStage extends Viewport3D implements Pinnable {
  idle: string;
  turnable: boolean;
  drift: number;
  glow: ColorRef | null;
  spin: number;
  highlight: ColorRef | null;
  onPartClick: ((part: string | null) => void) | null;
  outline: ColorRef | null;
  ghost: number;
  reveal: boolean;
  sparks: boolean;

  #src: string | null;
  #view: StageView;
  #parts: string[] | null;
  #selected: string | null = null;
  #hovered: string | null = null;
  #height = STAGE_HEIGHT;
  #pressAt: { x: number; y: number } | null = null;
  #washed = new Map<Mesh, Washed>();
  #revealStart: number | null = null;
  #sparkStart: number | null = null;
  #sparkCenter = new Vector3();
  #sparkReach = 0.5;
  /** Clips the figure to the reveal's front while it sweeps. */
  #clip = new ClippingGroup();
  #clipPlane = new Plane(new Vector3(0, -1, 0), 1e6);
  #figure = new Group();
  #glowLights = [new PointLight(0xffffff, 0, 14, 1.1), new PointLight(0xffffff, 0, 4, 1.4)];
  #sparkMesh: InstancedMesh | null = null;
  #sparkSeeds: Vector3[] = [];
  #model: Model | null = null;
  #instance: ModelInstance | null = null;
  #loading = 0;
  #clock = 0;
  #lastFrame = -1;
  #yaw = 0;
  #turn = 0;
  #dragX: number | null = null;
  #stopFrames: (() => void) | null = null;

  constructor(options: ModelStageOptions = {}) {
    const camera = options.camera ?? new PerspectiveCamera(28.6, 1, 0.1, 60);
    super({ clear: rgba(0, 0, 0, 0), ...options, camera, orbitOnDrag: false, zoomOnWheel: false });
    this.#src = options.src ?? null;
    this.idle = options.idle ?? "idle,Idle,stand,breath";
    this.turnable = options.turnable ?? true;
    this.drift = options.drift ?? 1;
    this.glow = options.glow ?? null;
    this.spin = options.spin ?? 0;
    this.highlight = options.highlight ?? null;
    this.onPartClick = options.onPartClick ?? null;
    this.outline = options.outline ?? null;
    this.ghost = options.ghost ?? 0;
    this.reveal = options.reveal ?? false;
    this.sparks = options.sparks ?? false;
    // Out of the tab order, and so never wearing a focus ring: a stage on a page is a picture you can
    // turn, not a field, and it takes no keys.
    this.tabStop = false;
    this.#view = options.view ?? "stage";
    this.#parts = partList(options.parts);
    this.#clip.clippingPlanes = [this.#clipPlane];
    this.#clip.add(this.#figure);
    this.scene.add(this.#clip);
    this.#glowLights[0]!.position.set(0, -2.2, -3.5);
    this.#glowLights[1]!.position.set(0, 0.25, 0.6);
    for (const light of this.#glowLights) this.scene.add(light);
    this.#placeCamera(stageCamera(this.#fov(), 0, this.drift));

    this.on("pointerdown", (e) => this.#grab(e));
    this.on("pointermove", (e) => this.#drag(e));
    this.on("pointerup", (e) => this.#release(e));
    this.on("pointerleave", () => {
      this.#release(null);
      this.#hover(null);
    });
    this.on("wheel", (e) => this.#zoom(e));
    this.#frame();
  }

  get view(): StageView {
    return this.#view;
  }
  set view(v: StageView) {
    if (v === this.#view) return;
    this.#view = v;
    this.#frame();
  }

  /** The parts a click can pick, as given. */
  get parts(): readonly string[] | null {
    return this.#parts;
  }
  set parts(v: readonly string[] | null) {
    this.#parts = partList(v);
    this.#selected = null;
    this.#hovered = null;
    this.#wash();
  }

  /** The part shown as picked. Set it to point at one from outside - a button, a variable. */
  get selected(): string | null {
    return this.#selected;
  }
  set selected(v: string | null) {
    const next = v && v.length > 0 ? v : null;
    if (next === this.#selected) return;
    this.#selected = next;
    this.#wash();
  }

  /** The part under the pointer, if any. (`hovered` is Control's: whether the pointer is over it.) */
  get hoveredPart(): string | null {
    return this.#hovered;
  }

  get src(): string | null {
    return this.#src;
  }
  set src(v: string | null) {
    if (v === this.#src) return;
    this.#src = v;
    this.#load();
  }

  /** The loaded model, once it has arrived. */
  get model(): Model | null {
    return this.#model;
  }

  /** The model's live copy - its skeleton and animation mixer - once it has arrived. */
  get instance(): ModelInstance | null {
    return this.#instance;
  }

  pinPoint(anchor: string): Point | null {
    const fraction = STAGE_ANCHORS[anchor];
    if (fraction === undefined || !this.#instance) return null;
    return this.project({ x: 0, y: this.#height * fraction, z: 0 });
  }

  /** The part under a point in this control, or null. */
  partAt(local: { x: number; y: number }): string | null {
    const instance = this.#instance;
    if (!instance || !this.#parts) return null;
    const parts = this.#parts[0] === "*" ? null : this.#parts;
    this.scene.updateMatrixWorld(true);
    for (const hit of this.raycasterAt(local).intersectObject(instance.root, true)) {
      const part = partOf(hit.object, parts, instance.root);
      if (part) return part;
    }
    return null;
  }

  override handlesDragAt(): boolean {
    return this.turnable;
  }

  protected override onEnvironmentChanged(): void {
    super.onEnvironmentChanged();
    this.#stopFrames?.();
    this.#stopFrames = null;
    const desktop = this.desktop;
    if (!desktop) return;
    this.#stopFrames = desktop.onFrame((now) => this.#step(now));
    if (!this.#model && this.#src) this.#load();
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const glow = resolveColorRef(this.glow, theme.palette);
    const breathe = 0.85 + 0.15 * Math.sin(this.#clock * 2.4);
    const [far, near] = this.#glowLights as [PointLight, PointLight];
    if (glow) {
      far.color.setRGB(glow.r, glow.g, glow.b, SRGBColorSpace);
      near.color.copy(far.color);
    }
    // Physical lights: candela, so the old engine's intensities are scaled to read the same at the figure.
    far.intensity = glow ? 30 * breathe : 0;
    near.intensity = glow ? 4 * breathe : 0;
    this.#wash(theme);
    this.#finish(theme);
    super.onPaint(rc, theme);
  }

  /** Release the model and its GPU buffers. */
  dispose(): void {
    this.#stopFrames?.();
    this.#stopFrames = null;
    this.#instance?.dispose();
    this.#instance = null;
    this.#model?.dispose();
    this.#model = null;
    this.#washed.clear();
    this.#sparkMesh?.geometry.dispose();
    this.#sparkMesh = null;
  }

  #fov(): number {
    return this.camera instanceof PerspectiveCamera ? (this.camera.fov * Math.PI) / 180 : 0.5;
  }

  #placeCamera(frame: { position: Vec3; target: Vec3 }): void {
    this.target.set(frame.target.x, frame.target.y, frame.target.z);
    this.camera.position.set(frame.position.x, frame.position.y, frame.position.z);
    this.camera.lookAt(this.target);
  }

  /** Advance the clip, the turn and the camera to the desktop's clock. */
  #step(now: number): boolean {
    if (!this.effectivelyVisible) return false;
    const reduced = this.desktop?.reducedMotion === true;
    const dt = this.#lastFrame < 0 ? 0 : Math.min(0.1, Math.max(0, now - this.#lastFrame));
    this.#lastFrame = now;
    this.#clock += reduced ? 0 : dt;

    this.#instance?.update(dt);
    if (this.#dragX === null && this.spin !== 0 && !reduced) this.#turn += this.spin * dt;
    let delta = this.#turn - this.#yaw;
    while (delta > Math.PI) delta -= Math.PI * 2;
    while (delta < -Math.PI) delta += Math.PI * 2;
    this.#yaw += delta * Math.min(1, dt * (this.#dragX === null ? 7 : 20));
    this.#figure.rotation.set(0, this.#yaw, 0);

    // An orbit's camera is the visitor's; only a stage's drifts on its own.
    if (this.#view === "stage") this.#placeCamera(stageCamera(this.#fov(), this.#clock, reduced ? 0 : this.drift));
    this.invalidate();
    return true;
  }

  #load(): void {
    const src = this.#src;
    const loading = ++this.#loading;
    this.#instance?.dispose();
    this.#instance = null;
    this.#model?.dispose();
    this.#model = null;
    this.#washed.clear();
    if (!this.desktop || !src) return;
    void Model.load(src).then(
      (model) => {
        // A newer src was set while this one was on its way.
        if (loading !== this.#loading) {
          model.dispose();
          return;
        }
        this.#model = model;
        const instance = model.instantiate();
        this.#figure.add(instance.root);
        for (const name of this.idle.split(",").map((s) => s.trim()).filter(Boolean)) {
          if (instance.play(name)) break;
        }
        this.#instance = instance;
        this.#frame();
        this.#wash();
        // Swept in from the floor, unless the visitor has asked for less movement.
        this.#revealStart = this.reveal && this.desktop?.reducedMotion !== true ? this.#clock : null;
        this.invalidate();
      },
      (error: unknown) => {
        console.warn(`webwin: ModelStage could not load ${src}:`, error);
      },
    );
  }

  /** Stand the model for the view it is in, and put the camera where that view starts. */
  #frame(): void {
    this.orbitOnDrag = this.#view === "orbit" && this.turnable;
    // Never the viewport's own wheel zoom: it takes every wheel turn, and a model on a page would then
    // stop the page scrolling wherever the pointer crossed it. `#zoom` takes Ctrl + wheel.
    this.zoomOnWheel = false;
    const instance = this.#instance;
    const model = this.#model;
    if (!instance || !model) return;
    const root = instance.root;
    if (this.#view === "orbit") {
      const fit = fitOrbit(model.bounds);
      root.scale.setScalar(fit.scale);
      root.position.set(fit.offset.x, fit.offset.y, fit.offset.z);
      this.#height = fit.height;
      const start = orbitStart(this.#fov(), fit.height, fit.radius);
      this.#placeCamera(start);
      this.minDistance = start.distance * 0.35;
      this.maxDistance = start.distance * 2.5;
    } else {
      const fit = fitModel(model.bounds);
      root.scale.setScalar(fit.scale);
      root.position.set(fit.offset.x, fit.offset.y, fit.offset.z);
      this.#height = STAGE_HEIGHT;
    }
    this.invalidate();
  }

  #partSets(): { picked: Set<Mesh>; hovered: Set<Mesh> } {
    const instance = this.#instance;
    if (!instance) return { picked: new Set(), hovered: new Set() };
    const parts = this.#parts && this.#parts[0] !== "*" ? this.#parts : null;
    return {
      picked: new Set((this.#selected ? meshesOf(instance.root, this.#selected, parts) : []) as Mesh[]),
      hovered: new Set((this.#hovered ? meshesOf(instance.root, this.#hovered, parts) : []) as Mesh[]),
    };
  }

  /**
   * Wash the hovered and the picked part in the highlight color. Each mesh gets a material of its own the
   * first time it is washed - a glTF shares one material between every primitive that uses it, and washing
   * the shared one would light up every wall painted the same color.
   */
  #wash(theme?: Theme): void {
    const instance = this.#instance;
    if (!instance) return;
    const palette = theme?.palette ?? this.desktop?.theme.palette;
    const tint: Color = (palette && resolveColorRef(this.highlight ?? "accent", palette)) || { r: 1, g: 0.8, b: 0.3, a: 1 };
    const tintColor = new ThreeColor().setRGB(tint.r, tint.g, tint.b, SRGBColorSpace);
    const { picked, hovered } = this.#partSets();
    // With an outline doing the pointing, the wash is only a hint; without one it is the pointing.
    const strong = this.outline ? 0.12 : 0.55;
    const soft = this.outline ? 0.05 : 0.25;
    // Everything else fades when a part is picked, so the picked one can be seen from inside.
    const faded = this.ghost > 0 && picked.size > 0 ? 1 - Math.min(1, this.ghost) * 0.85 : 1;
    instance.root.traverse((o) => {
      const mesh = o as Mesh;
      if (!mesh.isMesh || Array.isArray(mesh.material)) return;
      const amount = picked.has(mesh) ? strong : hovered.has(mesh) ? soft : 0;
      const alpha = picked.has(mesh) ? 1 : faded;
      let washed = this.#washed.get(mesh);
      if (amount === 0 && alpha === 1 && !washed) return;
      if (!washed) {
        const base = mesh.material as Washed["base"];
        washed = { base, own: base.clone() as Washed["own"] };
        mesh.material = washed.own;
        this.#washed.set(mesh, washed);
      }
      const { own, base } = washed;
      if (own.color && base.color) own.color.copy(base.color).lerp(tintColor, amount);
      if (own.emissive) {
        own.emissive.copy(base.emissive ?? new ThreeColor(0)).lerp(tintColor, amount);
        own.emissiveIntensity = Math.max(base.emissiveIntensity ?? 1, amount > 0 ? 0.6 : 0);
      }
      own.transparent = alpha < 1 || base.transparent;
      own.opacity = base.opacity * alpha;
      own.depthWrite = alpha < 1 ? false : base.depthWrite;
    });
    this.invalidate();
  }

  /** The outline, the reveal's sweep and the sparks, as they stand this frame. */
  #finish(theme: Theme): void {
    const renderer = this.renderer;
    const palette = theme.palette;
    const accent = resolveColorRef(this.highlight ?? "accent", palette) ?? palette.accent;
    const { picked, hovered } = this.#partSets();
    if (renderer) {
      const outline = this.outline === null ? null : (resolveColorRef(this.outline, palette) ?? accent);
      renderer.outline = outline ? { objects: [...picked, ...hovered], color: outline } : null;
    }

    const front = this.#revealStart === null ? null : revealFront(this.#clock - this.#revealStart, this.#height);
    if (front === null) this.#revealStart = null;
    // Keep what is below the front: distance -y + front is positive there.
    this.#clipPlane.constant = front ?? 1e6;

    const life = this.#sparkStart === null ? null : sparkLife(this.#clock - this.#sparkStart);
    if (life === null) {
      this.#sparkStart = null;
      if (this.#sparkMesh) this.#sparkMesh.visible = false;
      return;
    }
    const sparks = this.#sparksFor(accent);
    sparks.visible = true;
    const m = new Object3D();
    const fall = life * life;
    for (let i = 0; i < SPARKS; i++) {
      const seed = this.#sparkSeeds[i]!;
      m.position.copy(this.#sparkCenter).addScaledVector(seed, this.#sparkReach * (0.4 + life * 1.6));
      m.position.y += this.#sparkReach * (life * 0.8 - fall * 1.4);
      m.scale.setScalar(0.022 * (1 - life));
      m.updateMatrix();
      sparks.setMatrixAt(i, m.matrix);
    }
    sparks.instanceMatrix.needsUpdate = true;
  }

  #sparksFor(color: Color): InstancedMesh {
    if (!this.#sparkMesh) {
      const material = new MeshBasicMaterial({ blending: AdditiveBlending, transparent: true, depthWrite: false });
      this.#sparkMesh = new InstancedMesh(new IcosahedronGeometry(1, 0), material, SPARKS);
      this.#sparkMesh.frustumCulled = false;
      this.scene.add(this.#sparkMesh);
      for (let i = 0; i < SPARKS; i++) {
        this.#sparkSeeds.push(new Vector3(Math.random() - 0.5, Math.random() - 0.2, Math.random() - 0.5).normalize());
      }
    }
    // Bright enough past 1 that a bloom catches them.
    (this.#sparkMesh.material as MeshBasicMaterial).color.setRGB(color.r * 3, color.g * 3, color.b * 3, SRGBColorSpace);
    return this.#sparkMesh;
  }

  /** Where a burst for this part starts, and how far it reaches: the middle of its world bounds. */
  #aimSparks(part: string): void {
    const instance = this.#instance;
    if (!instance) return;
    const parts = this.#parts && this.#parts[0] !== "*" ? this.#parts : null;
    this.scene.updateMatrixWorld(true);
    const box = new Box3();
    for (const mesh of meshesOf(instance.root, part, parts)) box.expandByObject(mesh, true);
    if (box.isEmpty()) return;
    box.getCenter(this.#sparkCenter);
    const size = box.getSize(new Vector3());
    const half = Math.max(size.x, size.y, size.z) / 2;
    this.#sparkReach = Math.min(Math.max(half, 0.2), this.#height * 0.5 || 1);
    this.#sparkStart = this.#clock;
  }

  /**
   * Ctrl + wheel comes closer or goes back, in orbit view; a plain wheel is left for whatever is scrolling
   * the page - the convention a map embedded in a page has taught everybody.
   */
  #zoom(e: WheelEventArgs): void {
    if (this.#view !== "orbit" || !e.ctrlKey || e.deltaY === 0) return;
    const offset = this.camera.position.clone().sub(this.target);
    const distance = offset.length();
    if (distance <= 0) return;
    // Multiplicative, as the viewport's own is, so each notch moves the same proportion.
    const next = Math.min(this.maxDistance, Math.max(this.minDistance, distance * (e.deltaY > 0 ? 1.12 : 1 / 1.12)));
    this.camera.position.copy(this.target).addScaledVector(offset, next / distance);
    this.invalidate();
    e.handled = true;
  }

  #hover(part: string | null): void {
    if (part === this.#hovered) return;
    this.#hovered = part;
    this.cursor = part ? "pointer" : "default";
    this.#wash();
  }

  #grab(e: MouseEventArgs): void {
    this.#pressAt = { x: e.x, y: e.y };
    if (!this.turnable || this.#view !== "stage") return;
    this.#dragX = e.x;
    this.desktop?.input.setCapture(this);
  }

  #drag(e: MouseEventArgs): void {
    if (this.#dragX === null) {
      if (!this.#pressAt && this.#parts) this.#hover(this.partAt(e));
      return;
    }
    this.#turn += (e.x - this.#dragX) * TURN_PER_PIXEL;
    this.#dragX = e.x;
  }

  #release(e: MouseEventArgs | null): void {
    const press = this.#pressAt;
    this.#pressAt = null;
    if (this.#dragX !== null) {
      this.#dragX = null;
      this.desktop?.input.releaseCapture(this);
    }
    if (!e || !press || !this.#parts) return;
    if (Math.hypot(e.x - press.x, e.y - press.y) > CLICK_SLOP) return;
    const part = this.partAt(e);
    this.selected = part;
    if (part && this.sparks && this.desktop?.reducedMotion !== true) this.#aimSparks(part);
    this.onPartClick?.(part);
  }
}

/** A parts list with the blanks taken out; null when nothing is left. */
function partList(parts: readonly string[] | null | undefined): string[] | null {
  const list = (parts ?? []).map((p) => p.trim()).filter((p) => p.length > 0);
  return list.length > 0 ? list : null;
}
